/**
 * QuestionnaireProgress — step progress indicator with dots and completion bar.
 */
import React from 'react';
import type { QuestionnaireStep } from '@/types/pm-types';

export interface QuestionnaireProgressProps {
  steps: QuestionnaireStep[];
  currentStepIndex: number;
  visitedStepIds: string[];
  completedStepIds: string[];
  accentColor?: string;
}

export const QuestionnaireProgress: React.FC<QuestionnaireProgressProps> = ({
  steps,
  currentStepIndex,
  visitedStepIds,
  completedStepIds,
  accentColor = '#2563eb',
}) => {
  const totalSteps = steps.length;
  const completionPercent = totalSteps > 0
    ? Math.round((completedStepIds.length / totalSteps) * 100)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 4px' }}>
      {/* Step dots */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {steps.map((step, index) => {
          const isCurrent = index === currentStepIndex;
          const isCompleted = completedStepIds.includes(step.id);
          const isVisited = visitedStepIds.includes(step.id);

          let bgColor = '#e5e7eb'; // default gray
          if (isCurrent) bgColor = accentColor;
          else if (isCompleted) bgColor = '#10b981'; // green
          else if (isVisited) bgColor = '#93c5fd'; // light blue

          return (
            <React.Fragment key={step.id}>
              {index > 0 && (
                <div style={{
                  flex: 1,
                  height: 2,
                  maxWidth: 32,
                  background: isCompleted || isVisited ? accentColor : '#e5e7eb',
                  borderRadius: 1,
                  transition: 'background 0.2s',
                }} />
              )}
              <div
                title={step.title}
                style={{
                  width: isCurrent ? 12 : 8,
                  height: isCurrent ? 12 : 8,
                  borderRadius: '50%',
                  background: bgColor,
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                {isCompleted && !isCurrent && (
                  <span style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 6,
                    color: '#fff',
                    lineHeight: 1,
                  }}>
                    ✓
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress bar + label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          flex: 1,
          height: 3,
          background: '#e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${completionPercent}%`,
            background: accentColor,
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }} />
        </div>
        <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
          Step {currentStepIndex + 1} of {totalSteps}
        </span>
      </div>
    </div>
  );
};

QuestionnaireProgress.displayName = 'QuestionnaireProgress';
